import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeletePortalRole } from '@/hooks/react-query/usePortalRoles';
import type { PortalRole } from '@/services/portalRoleService';

interface DeletePortalRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: PortalRole | null;
  onRoleDeleted?: () => void;
}

export function DeletePortalRoleDialog({
  open,
  onOpenChange,
  role,
  onRoleDeleted,
}: DeletePortalRoleDialogProps) {
  const deleteRoleMutation = useDeletePortalRole();

  const handleDelete = async () => {
    if (!role) return;

    try {
      await deleteRoleMutation.mutateAsync(role.id);
      onOpenChange(false);
      onRoleDeleted?.();
    } catch {
      // Error is handled by mutation hook
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Portal Role</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this role?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="font-medium text-gray-900">{role.display_name}</p>
            {role.description && (
              <p className="text-sm text-gray-500 mt-1">{role.description}</p>
            )}
          </div>
          <p className="text-sm text-amber-600">
            Warning: Any investor contacts assigned to this role will lose their
            portal access. This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteRoleMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteRoleMutation.isPending}
          >
            {deleteRoleMutation.isPending ? 'Deleting...' : 'Delete Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
